import { HttpErrorHandler,
         HttpRequestError,
         toHttpRequest, 
         ConfigService,
         ServerConnector,
         WidgetConfigEntity,
         WidgetConfigFactory,
         WidgetInjectionTypeEnum,
         BotInitializationService,
         SessionEntity }                      from '../../..';


export interface IConversationSessionEntityActions {
    retrieveWidgetConfigParameters(storeFunctions: any): any;
    setConfigParamsAvailable(storeFunctions: any): any;
    setIntegrationMode(storeFunctions: any, integrationMode: WidgetInjectionTypeEnum): any;
    getConversationSessionId(storeFunctions: any, payload: any): any;
    setConversationPanelState(storeFunctions: any, payload: any): any;
    setConversationNextMessageWatermark(storeFunctions: any, payload: any): any;
    playEmotion(storeFunctions: any, payload: any): any;
    logSatisfaction(storeFunctions: any, payload: any): any;
    toggleIntroductionSentenceShownState(storeFunctions: any): any;
    retrieveSuggestedQuestions(storeFunctions: any): any;
    setSessionFeedbackScoreAvailable(storeFunctions: any): any;
}

export const ConversationSessionEntityActions: IConversationSessionEntityActions = {
    
    /**
     * Action definition triggered at chatbot widget initialization. Gets the widget configuration parameters from IFAQ server.
     * 
     * @param storeFunctions - Commit function to apply changes into the store.
     */
    async retrieveWidgetConfigParameters({ commit }) {
        const faqId: string | undefined = ConfigService.faqId;
        commit('SET_BOT_PARAMS_REQUEST_IN_PROGRESS');
        
        if (!faqId) {

          // handle chatbot initialization configuration failure
          const err: HttpRequestError = HttpErrorHandler.handleServerError( { errcode: 1, message: 'Missing chatbot FAQ ID, unable to retrieve widget parameters.' });
          commit('FETCH_BOT_PARAMS_REQUEST_ERROR', { err });

        } else {
            const urlPath = `faq/${ConfigService.faqId}/chatbotinterfaceconfig?{}`;

            // tslint:disable-next-line: prefer-const
            const [err, results]: [HttpRequestError | undefined, any] = await toHttpRequest(ServerConnector.getIFaqServerConnectorInstance().get(urlPath));
            
            if (err) {
            // handle retrieve widget configuration parameters request failure
            commit('FETCH_BOT_PARAMS_REQUEST_ERROR', { err });
            } else {
                if (results && results.result && results.result.chatbotconfiguration) {
                    const widgetConfig: WidgetConfigEntity | null = WidgetConfigFactory.buildWidgetConfigurationEntity(results.result.chatbotconfiguration);
                    if (widgetConfig) {

                        // keep data in memory and not into the state to prevent useless persistance of session data
                        ConfigService.setWidgetConfigEntity(widgetConfig);
                        // override specific settings from integration settings object
                        ConfigService.overrideIFaqWidgetConfigParams();

                        window.botSettings.customChatbotTheme = widgetConfig.params.get('customChatbotTheme');
                        commit('SET_CONFIG_PARAMS_AVAILABLE');

                    } else {
                        // handle empty widget configuration parameters list error
                        const err: HttpRequestError = HttpErrorHandler.handleServerError( { errcode: 2, message: 'Service momentanément indisponible' });
                        commit('FETCH_BOT_PARAMS_REQUEST_ERROR', { err });
                    }
                } else {
                    // handle empty widget configuration parameters list error
                    const err: HttpRequestError = HttpErrorHandler.handleServerError( { errcode: 2, message: 'Service momentanément indisponible' });
                    commit('FETCH_BOT_PARAMS_REQUEST_ERROR', { err });
                }
            }
        }
    },

    /**
     * Action definition triggered at chatbot widget initialization. Sets widget params retrieved indicator.
     * 
     * @param storeFunctions - Commit function to apply changes into the store.
     */
    setConfigParamsAvailable({ commit }) { 
        commit('SET_CONFIG_PARAMS_AVAILABLE');
    },

    /**
     * Action definition triggered to set the integration mode to use.
     * 
     * @param storeFunctions - Commit function to apply changes into the store.
     * @param integrationMode - The integration mode to use on chat display.
     */
    setIntegrationMode({ commit }, integrationMode) { 
        commit('SET_INTEGRATION_MODE', integrationMode);
    },

    /**
     * Action definition triggered at chatbot widget initialization. Gets a unique server bot session identifier and 
     * create an initialized dialog messages list.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - The chatbox widget configuration retrieved from server.
     */
     async getConversationSessionId({ commit, dispatch }, { widgetConfig, isRefresh }) {
        if (widgetConfig) {
            // set initialization in progress indicator in chatbot conversation window
            commit('FETCH_SESSION_ID_REQUEST_IN_PROGRESS');

            const urlPath: string = `/line/synapse/bot/${ConfigService.synapseBotId}/conversation?new`;
            const guestId: string = BotInitializationService.retrieveGuestId(widgetConfig.params.get('userId'));

            // tslint:disable-next-line: prefer-const
            const [err, results]: [HttpRequestError | undefined, any] = await toHttpRequest(ServerConnector.getBotServerConnectorInstance()
                                                                                                           .put(urlPath, { 'userId': guestId }));
            if (err) {
                // handle initialization session request failure
                commit('FETCH_SESSION_ID_REQUEST_ERROR', { err });
                // notify integration app that session couldn't be started
                window.postMessage('chat_session_init_error', '*');
            } else {
                if (results && results.result) {
                    const integrationMode: WidgetInjectionTypeEnum = (ConfigService.isEmbeddedModeConfigured()) ? WidgetInjectionTypeEnum.INTEGRATED
                                                                                                                : WidgetInjectionTypeEnum.STANDARD
                    const sessionInfo: SessionEntity = new SessionEntity(results.result, 
                                                                        guestId, 
                                                                        widgetConfig.params.get('botName'), 
                                                                        window.location.href, 
                                                                        integrationMode, 
                                                                        0,
                                                                        isRefresh);
                    commit('SET_NEW_SESSION_ID', sessionInfo);

                    // initialize conversation session and display the first message if configured
                    dispatch('messages/displayIntroductionSentence', { sessionInfo, isRefresh }, { root: true });
                    // initialize framework initial command if configured
                    BotInitializationService.initNotificationPolling(dispatch, sessionInfo)
                    // notify integration app that session has started
                    window.postMessage('chat_session_initialized', '*');
                } else {
                    // handle chatbot initialization request failure
                    const err = HttpErrorHandler.handleServerError( { errcode: 2, message: 'Service momentanément indisponible' });
                    commit('FETCH_BOT_PARAMS_REQUEST_ERROR', { err });
                    // notify integration app that session couldn't be started
                    window.postMessage('chat_session_init_error', '*');
                }
            }
        } else {
            // handle missing chatbot configuration initialization
            const err = HttpErrorHandler.handleServerError( { errcode: 100, message: 'Missing widget configuration' });
            commit('FETCH_BOT_PARAMS_REQUEST_ERROR', { err });
        }
    },

    /**
     * Action definition triggered to retrieve the list of suggested questions
     * 
     * @param storeFunctions - Commit function to apply changes into the store.
     */
    async retrieveSuggestedQuestions({ commit }) {
        const urlPath = `faq/${ConfigService.faqId}/autocomplete_questions`;
        
        // tslint:disable-next-line: prefer-const
        const [err, results]: [HttpRequestError | undefined, any] = await toHttpRequest(ServerConnector.getIFaqServerConnectorInstance().get(urlPath));
        
        if (err) {
            ConfigService.initSuggestedQuestionsList([]);
        } else if (results && results.list) {
            const suggestedQuestions: string[] = results.list.map((suggestedQuestion: any) => suggestedQuestion.text);
            commit('SET_SUGGESTIONS_LIST', suggestedQuestions);
        } else {
            commit('SET_SUGGESTIONS_LIST', []);
        }
    },

    /**
     * Action to log satisfaction score
     * 
     * @param storeFunctions - Commit function to apply changes into the store.
     * @param payload - The score set by the user for the current conversation session.
     */
    async logSatisfaction({ commit }, { sessionId, score }) {
        const urlPath = `session/${sessionId}/logsatisfaction/faq`;

        // tslint:disable-next-line: prefer-const
        const [err, results]: [HttpRequestError | undefined, any] = await toHttpRequest(ServerConnector.getIFaqServerConnectorInstance()
                                                                                                       .post(urlPath, { 'satisfaction': score }));
        
        if (err) {
            // Nothing to do
        } else if (results && results.statusCode === 0) {
            commit('UPDATE_SESSION_SCORE', score);
        }                                                                                         
    },

    /**
     * Action definition triggered when user clic on presentation avatar to display chatbox panel in standard mode or when the 
     * he sends a message to bot server.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - TRUE when the cionversation panel should be opened. FALSE otherwise.
     */
    setConversationPanelState({ commit }, isOpen) { 
        commit('UPDATE_SESSION_CONVERSATION_STATUS', isOpen);
    },

    /**
     * Action definition triggered when user clic on presentation avatar to display chatbox panel in standard mode or when the 
     * he sends a message to bot server.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - The watermark to use in next message.
     */
    setConversationNextMessageWatermark({ commit }, nextMsgWatermark) { 
        commit('UPDATE_NEXT_MESSAGE_WATERMARK', nextMsgWatermark);
    },

    /**
     * Action definition triggered when presentation message has been displayed.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     */
    toggleIntroductionSentenceShownState({ commit }) { 
        commit('TOGGLE_INTRODUCTION_SENTENCE_SHOWN');
    },

    /**
     * Action definition triggered when an emotion should be played immediately.
     * 
     * @param storeFunctions - Commit function to apply changes into the store.
     * @param emotionToPlay - The emotion to play.
     */
    playEmotion({ commit }, emotionToPlay) { 
        commit('UPDATE_EMOTION_TO_PLAY', emotionToPlay);
    },

    /**
     * Action definition triggered when the server allows feedback session score recording.
     * 
     * @param storeFunctions - Commit function to apply changes into the store.
     */
    setSessionFeedbackScoreAvailable({ commit }) { 
        commit('UPDATE_SESSION_SCORE', 0);
    },
};
